import { inject, Injectable, signal } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { NavigationItem } from 'app/core/interfaces/navigation-item';
import { Permission } from 'app/core/security/interfaces';
import { ROLES_PERMISSIONS } from 'app/core/security/roles-permissions-distribution';
import { hasAction, isSameResource } from 'app/services/permission.service';
import { toPairs } from 'lodash';
import { UserService } from '../sho_management_pro/user/services/user.service';
import { SHONavigationItems } from './navigation-items';

@Injectable({
    providedIn: 'root',
})
export class NavigationService {
    _userService = inject(UserService);
    mode = signal<'sm' | 'lg' | null>(null);

    getSideBarItems(): {
        lgMenuData: FuseNavigationItem[];
        smMenuData: FuseNavigationItem[];
        settingChildren: FuseNavigationItem[] | null;
    } {
        const userRole = this._userService.getAuthenticatedUserRole();

        // Get user permissions based on role
        const permissions = ROLES_PERMISSIONS[userRole];

        // Get navigation items based on permissions
        this.mode.set('lg');
        const lgNav = this.getNavigationItems(permissions);

        this.mode.set('sm');
        const smNav = this.getNavigationItems(permissions);

        return {
            lgMenuData: lgNav,
            smMenuData: smNav,
            settingChildren: null,
        };
    }

    /**
     * @description
     * Get a list of all the potential navigation items
     * For each permission, verify if it has access to a
     * corresponding nav item
     */
    getNavigationItems(permissions: Permission[]): NavigationItem[] {
        const navItems = toPairs(SHONavigationItems);

        let currentNavItems: NavigationItem[] = [];

        permissions.forEach((permission: Permission) =>
            currentNavItems.push(...this.filterNavItems(permission, navItems))
        );

        return currentNavItems;
    }

    /**
     * @description
     * Get Navigation Items
     * Also choose among basic and collapsable to fit the behavior
     */
    filterNavItems(
        permission: Permission,
        navItems: [string, NavigationItem][]
    ): NavigationItem[] {
        let _navItems: NavigationItem[] = [];
        navItems.forEach(([_, navItem]: [string, NavigationItem]) => {
            if (
                !hasAction(permission, navItem.permission) ||
                !isSameResource(navItem.permission, permission)
            )
                return;

            const itemType = navItem.type;

            if (itemType === 'basic') {
                _navItems.push(navItem);
            }

            if (itemType === 'collapsable') {
                const disaggregatedItems = this.disaggregateCollapsableItems(
                    navItem,
                    permission
                );

                _navItems.push(...disaggregatedItems);
            }
        });

        return _navItems;
    }

    disaggregateCollapsableItems(
        navItem: NavigationItem,
        permission: Permission
    ): any[] {
        let disaggregatedItems: NavigationItem[] = [];

        // Before deconstruct the collapsable add a divider
        disaggregatedItems.push({ type: 'divider' });

        const children = navItem.children || [];

        // Filter children based on children permissions
        const validChildren = permission.children
            ? this.getChildrenNavItems(children, permission.children)
            : children;

        this.mode() === 'lg'
            ? disaggregatedItems.push({
                  ...navItem,
                  children: validChildren,
              })
            : disaggregatedItems.push(...validChildren);

        return disaggregatedItems;
    }

    getChildrenNavItems(
        navItems: NavigationItem[],
        permissions: Permission[]
    ): any[] {
        let filter = [];
        navItems.forEach((navItem) => {
            permissions.forEach((permission) => {
                isSameResource(navItem.permission, permission) &&
                hasAction(permission, navItem.permission)
                    ? filter.push(navItem)
                    : null;
            });
        });

        return filter;
    }
}
